import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useObOneOnOnesApi from './config';
import { parseTableParams, formatDateTime } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const Modal = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center; justify-content: center; z-index: 1300; padding: 24px;
`;
const ModalCard = styled.div`
  background: #fff; border-radius: 8px; max-width: 520px; width: 100%; padding: 24px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
`;
const Field = styled.div` display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; font-size: 14px; `;
const FieldLabel = styled.label` color: #64748b; font-weight: 500; `;
const FieldInput = styled.input` padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 14px; `;
const FieldText = styled.textarea` padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 14px; resize: vertical; min-height: 60px; font-family: inherit; `;
const Actions = styled.div` display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; `;
const DetailSection = styled.div` margin-bottom: 14px; `;
const DetailLabel = styled.div` font-size: 12px; font-weight: 600; color: #64748b; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.3px; `;
const DetailText = styled.div` font-size: 14px; color: #1e293b; white-space: pre-wrap; `;
const AckBadge = styled.div`
  display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600;
  padding: 6px 12px; border-radius: 6px;
  background: ${({ $ok }) => ($ok ? '#E8F7EE' : '#FFF3CD')};
  color: ${({ $ok }) => ($ok ? '#1B7A3E' : '#856404')};
`;
const ActionItemRow = styled.li` font-size: 14px; color: #1e293b; margin-bottom: 4px; `;

const parseActionItems = (json) => {
  if (!json) return [];
  try { const v = JSON.parse(json); return Array.isArray(v) ? v : []; } catch { return []; }
};
// #72 — yerel Primary/Secondary (kopyalanmis blok — onboarding-*/ats-*
// sayfalarinda ayni tarif) kaldirildi, paylasilan Button'a baglandi
// (bkz. t6-report.md).

export default function ObOneOnOnesPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('OB') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  // Görüşmenin SONUCU yönetici tarafında da görünmeli (müşteri geri bildirimi):
  // yönetici notu, aksiyon maddeleri, çalışan yansıması ve teyit damgası.
  const [detailDialog, setDetailDialog] = useState({ isOpen: false, row: null });
  const [conductDialog, setConductDialog] = useState({ isOpen: false, id: null, person: '', notes: '' });
  const [cancelDialog, setCancelDialog] = useState({ isOpen: false, id: null, person: '', reason: '' });
  const [rescheduleDialog, setRescheduleDialog] = useState({ isOpen: false, id: null, person: '', scheduledAt: '' });
  const [noShowDialog, setNoShowDialog] = useState({ isOpen: false, id: null, person: '' });
  const [busy, setBusy] = useState(false);

  // drawer: { mode: 'create' }
  const [drawer, setDrawer] = useState(null);

  const { fetching, data, pagination, columns: baseColumns, objectSchema, create, conduct, cancel, markNoShow, reschedule } = useObOneOnOnesApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const handleNewButtonClick = useCallback(() => setDrawer({ mode: 'create' }), []);

  const handleSave = useCallback(async (formData) => {
    const success = await create(formData);
    if (success) setDrawer(null);
    return success;
  }, [create]);

  const buildActions = useCallback((row) => {
    if (!row) return [];
    const actions = [];
    actions.push({ type: 'details', label: t('onboardingOneOnOne.action.details'),
      onClick: () => setDetailDialog({ isOpen: true, row }) });
    if (!writable) return actions;
    if (row.status === 'scheduled') {
      actions.push({ type: 'divider' });
      actions.push({ type: 'edit', label: t('onboardingOneOnOne.action.conduct'),
        onClick: () => setConductDialog({ isOpen: true, id: row.id, person: row.personFullName, notes: '' }) });
      actions.push({ type: 'details', label: t('onboardingOneOnOne.action.reschedule'),
        onClick: () => setRescheduleDialog({ isOpen: true, id: row.id, person: row.personFullName, scheduledAt: '' }) });
      actions.push({ type: 'details', label: t('onboardingOneOnOne.action.noShow'),
        onClick: () => setNoShowDialog({ isOpen: true, id: row.id, person: row.personFullName }) });
      actions.push({ type: 'divider' });
      actions.push({ type: 'delete', label: t('onboardingOneOnOne.action.cancel'),
        onClick: () => setCancelDialog({ isOpen: true, id: row.id, person: row.personFullName, reason: '' }) });
    }
    return actions;
  }, [t, writable]);

  const columns = useMemo(() => {
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions]);

  return (
    <Container>
      <GenericFormDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        title={t('onboardingOneOnOne.new')}
        schema={objectSchema}
        initialValues={null}
        onSave={handleSave}
      />

      {/* Görüşme sonucu — yönetici tarafı detay görünümü */}
      <Modal $open={detailDialog.isOpen}>
        {detailDialog.row && (
          <ModalCard style={{ maxWidth: 620, maxHeight: '86vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 4px' }}>{detailDialog.row.personFullName}</h3>
            <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 16 }}>
              {t(`onboardingOneOnOne.period.${detailDialog.row.period}`)}
              {' · '}{t(`onboardingOneOnOne.status.${detailDialog.row.status}`)}
              {' · '}{formatDateTime(detailDialog.row.conductedAt || detailDialog.row.scheduledAt, lang)}
            </div>

            {detailDialog.row.managerNotesMarkdown && (
              <DetailSection>
                <DetailLabel>{t('onboardingOneOnOne.detail.managerNotes')}</DetailLabel>
                <DetailText>{detailDialog.row.managerNotesMarkdown}</DetailText>
              </DetailSection>
            )}

            {parseActionItems(detailDialog.row.actionItemsJson).length > 0 && (
              <DetailSection>
                <DetailLabel>{t('onboardingOneOnOne.detail.actionItems')}</DetailLabel>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {parseActionItems(detailDialog.row.actionItemsJson).map((a, i) => (
                    <ActionItemRow key={a.id || i}>
                      {a.text}
                      {a.status === 'done' ? ' ✓' : ''}
                    </ActionItemRow>
                  ))}
                </ul>
              </DetailSection>
            )}

            {detailDialog.row.employeeReflectionMarkdown && (
              <DetailSection>
                <DetailLabel>{t('onboardingOneOnOne.detail.employeeReflection')}</DetailLabel>
                <DetailText>{detailDialog.row.employeeReflectionMarkdown}</DetailText>
              </DetailSection>
            )}

            {detailDialog.row.status === 'conducted' && (
              <DetailSection>
                <AckBadge $ok={!!detailDialog.row.employeeAcknowledgedAt}>
                  {detailDialog.row.employeeAcknowledgedAt
                    ? `${t('onboardingOneOnOne.detail.employeeAckAt')} · ${formatDateTime(detailDialog.row.employeeAcknowledgedAt, lang)}`
                    : t('onboardingOneOnOne.detail.employeeNotAcked')}
                </AckBadge>
              </DetailSection>
            )}

            {detailDialog.row.cancellationReason && (
              <DetailSection>
                <DetailLabel>{t('onboardingOneOnOne.cancelReason')}</DetailLabel>
                <DetailText>{detailDialog.row.cancellationReason}</DetailText>
              </DetailSection>
            )}

            <Actions>
              <Button variant="outline" onClick={() => setDetailDialog({ isOpen: false, row: null })}>{t('common.close')}</Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      <Modal $open={conductDialog.isOpen}>
        {conductDialog.isOpen && (
          <ModalCard>
            <h3 style={{ margin: '0 0 16px' }}>{t('onboardingOneOnOne.action.conduct')} — {conductDialog.person}</h3>
            <Field>
              <FieldLabel>{t('onboardingOneOnOne.notesRequired')}</FieldLabel>
              <FieldText value={conductDialog.notes}
                onChange={(e) => setConductDialog({ ...conductDialog, notes: e.target.value })} />
            </Field>
            <Actions>
              <Button variant="outline" onClick={() => setConductDialog({ isOpen: false, id: null, person: '', notes: '' })}>{t('common.cancel')}</Button>
              <Button disabled={busy || !conductDialog.notes.trim()} onClick={async () => {
                setBusy(true);
                await conduct(conductDialog.id, { managerNotesMarkdown: conductDialog.notes || undefined });
                setConductDialog({ isOpen: false, id: null, person: '', notes: '' });
                setBusy(false);
              }}>{busy ? '...' : t('common.confirm')}</Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      <Modal $open={rescheduleDialog.isOpen}>
        {rescheduleDialog.isOpen && (
          <ModalCard>
            <h3 style={{ margin: '0 0 16px' }}>{t('onboardingOneOnOne.action.reschedule')} — {rescheduleDialog.person}</h3>
            <Field>
              <FieldLabel>{t('onboardingOneOnOne.scheduledAt')}</FieldLabel>
              <FieldInput type="datetime-local" value={rescheduleDialog.scheduledAt}
                onChange={(e) => setRescheduleDialog({ ...rescheduleDialog, scheduledAt: e.target.value })} />
            </Field>
            <Actions>
              <Button variant="outline" onClick={() => setRescheduleDialog({ isOpen: false, id: null, person: '', scheduledAt: '' })}>{t('common.cancel')}</Button>
              <Button disabled={busy || !rescheduleDialog.scheduledAt} onClick={async () => {
                setBusy(true);
                await reschedule(rescheduleDialog.id, rescheduleDialog.scheduledAt);
                setRescheduleDialog({ isOpen: false, id: null, person: '', scheduledAt: '' });
                setBusy(false);
              }}>{busy ? '...' : t('common.confirm')}</Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      <Modal $open={cancelDialog.isOpen}>
        {cancelDialog.isOpen && (
          <ModalCard>
            <h3 style={{ margin: '0 0 16px' }}>{t('onboardingOneOnOne.action.cancel')} — {cancelDialog.person}</h3>
            <Field>
              <FieldLabel>{t('onboardingOneOnOne.cancelReason')}</FieldLabel>
              <FieldText value={cancelDialog.reason}
                onChange={(e) => setCancelDialog({ ...cancelDialog, reason: e.target.value })} />
            </Field>
            <Actions>
              <Button variant="outline" onClick={() => setCancelDialog({ isOpen: false, id: null, person: '', reason: '' })}>{t('common.cancel')}</Button>
              <Button disabled={busy} onClick={async () => {
                setBusy(true);
                await cancel(cancelDialog.id, cancelDialog.reason || undefined);
                setCancelDialog({ isOpen: false, id: null, person: '', reason: '' });
                setBusy(false);
              }}>{busy ? '...' : t('common.confirm')}</Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={noShowDialog.isOpen}
        title={t('onboardingOneOnOne.action.noShow')}
        message={t('onboardingOneOnOne.noShowConfirm', { person: noShowDialog.person })}
        onConfirm={async () => { setBusy(true); await markNoShow(noShowDialog.id); setNoShowDialog({ isOpen: false, id: null, person: '' }); setBusy(false); }}
        onCancel={() => setNoShowDialog({ isOpen: false, id: null, person: '' })}
        loading={busy}
      />

      {useMemo(() => (
        <DataTable
          title={t('onboardingOneOnOne.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, writable, handleNewButtonClick,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
